import React from 'react';
import DashesIfNullOrUndefined from '../../components/DashesIfNullOrUndefined';
import messages from './messages';
import {
  Button,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { HouseholdSection } from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import ServiceAnimalIndicator from './ServiceAnimalIndicator';
import changeHeadOfHouseholdMessages from './ChangeHeadOfHousehold/messages';
import { useEditHouseholdMemberDatesEnabled } from '../ResidentProfile/hooks';

// Matches the headers array below -- the trailing action row spans both of
// them (this table has no Status/Start/End Date columns pre-move-in).
const HOUSEHOLD_TABLE_COLUMN_COUNT = 2;

type Props = {
  editHouseholdMember: Function,
  householdMembers: Array<Object>,
  showChangeHeadOfHouseholdButton?: boolean,
  isChangeHeadOfHouseholdDisabled?: boolean,
  hasOpenCertsBlockingChangeHeadOfHousehold?: boolean,
  onOpenChangeHeadOfHousehold?: Function,
};

export default function ApplicantHousehold({
  intl,
  householdMembers,
  editHouseholdMember,
  showChangeHeadOfHouseholdButton,
  isChangeHeadOfHouseholdDisabled,
  hasOpenCertsBlockingChangeHeadOfHousehold,
  onOpenChangeHeadOfHousehold,
}: Props) {
  const isEditHouseholdMemberDatesEnabled =
    useEditHouseholdMemberDatesEnabled();

  // Remove pet rows when the separate Pets section is enabled.
  const visibleHouseholdMembers = isEditHouseholdMemberDatesEnabled
    ? householdMembers.filter((member) => member.type !== 'pet')
    : householdMembers;

  // Mirrors ResidentHousehold.js's own sort: the Head of Household must lead
  // the list, not wherever fetch/creation order left them, since a Change
  // Head of Household swap otherwise leaves the newly-promoted member where
  // they already were. Array#sort is stable, so this only moves the primary
  // member and leaves everyone else's relative order untouched.
  const sortedHouseholdMembers = [...visibleHouseholdMembers].sort(
    (a, b) => Number(Boolean(b.isPrimary)) - Number(Boolean(a.isPrimary)),
  );

  const rows = sortedHouseholdMembers.map((member) => {
    const isPet = member.type === 'pet';

    return (
      <TableRow
        key={member.id}
        sx={{ backgroundColor: 'transparent !important' }}
      >
        <TableCell>
          {isPet ? (
            <DashesIfNullOrUndefined data={member.name} />
          ) : (
            <a onClick={() => editHouseholdMember(member)}>
              <DashesIfNullOrUndefined data={member.name} />
            </a>
          )}
        </TableCell>
        <TableCell>
          <Stack direction={'row'} spacing={0.5} alignItems={'center'}>
            <span>{member.relationship}</span>

            {member.isServiceAnimal && <ServiceAnimalIndicator />}
          </Stack>
        </TableCell>
      </TableRow>
    );
  });

  // Same trailing-row placement/Tooltip pattern as ResidentHousehold's own
  // Change Head of Household button -- duplicated rather than shared since
  // the two tables have a different column count (colSpan) and this table
  // has no Status/Start/End Date rendering to entangle with.
  if (showChangeHeadOfHouseholdButton) {
    rows.push(
      <TableRow
        key="change-head-of-household"
        sx={{ backgroundColor: 'transparent !important' }}
      >
        <TableCell colSpan={HOUSEHOLD_TABLE_COLUMN_COUNT}>
          <Tooltip
            placement="top"
            containerSX={{ width: '100%' }}
            title={
              isChangeHeadOfHouseholdDisabled
                ? intl.formatMessage(
                    hasOpenCertsBlockingChangeHeadOfHousehold
                      ? changeHeadOfHouseholdMessages.openCertsTooltip
                      : changeHeadOfHouseholdMessages.noOtherAdultAvailableTooltip,
                  )
                : ''
            }
          >
            <Button
              variant="primarySubtle"
              fullWidth
              onClick={onOpenChangeHeadOfHousehold}
              disabled={isChangeHeadOfHouseholdDisabled}
              data-testid="change-head-of-household-button"
            >
              {intl.formatMessage(
                changeHeadOfHouseholdMessages.changeHeadOfHousehold,
              )}
            </Button>
          </Tooltip>
        </TableCell>
      </TableRow>,
    );
  }

  return (
    <HouseholdSection
      intl={intl}
      headers={[messages.name, messages.relation]}
      rows={rows}
    ></HouseholdSection>
  );
}
